import React, { ReactElement } from 'react';
import styled from '@emotion/styled';

import { spacing, getBorders, getBackgroundTheme } from '../../style';
import { Title, Header } from '../text';
import EditButton from '../buttons/ProfileEditButton';
import { IPadding, IPanelProps, IStyledPanel } from './types/Panel';
import { CustomStyledProps } from 'style/style';
import breakpoints from 'theme/material/breakpoints';

const PADDING: IPadding = {
  none: 0,
  small: spacing.SM,
  medium: spacing.MD,
  large: spacing.LG,
};

const PANEL_STYLE = (panelStyle: string | number, isLogin: boolean, isOtherProfile: boolean) => {
  const styles = {
    default: {
      border: getBorders().DEFAULT,
      boxShadow: '0px 1px 4px 0px rgba(0,0,0,.04)',
      borderRadius: '10px',
      backgroundColor: getBackgroundTheme().block,
      [breakpoints.down(768.05)]: {
        border: isLogin ? '1px solid #DAE1E9' : getBorders().DEFAULT,
        boxShadow: '0px 1px 4px 0px rgba(0,0,0,.04)',
        ...spacing.MARGIN_BOTTOM_SM,
      },
    },
    white: {
      border: getBorders().DEFAULT,
      boxShadow: '0px 1px 4px 0px rgba(0,0,0,.04)',
      borderRadius: '10px',
      backgroundColor: getBackgroundTheme().block2,
      [breakpoints.down(768.05)]: {
        backgroundColor: isOtherProfile ? getBackgroundTheme().block2 : getBackgroundTheme().block3,
        border: isLogin ? '1px solid #DAE1E9' : getBorders().DEFAULT,
        boxShadow: '0px 1px 4px 0px rgba(0,0,0,.04)',
      },
    },
    transparent: {
      border: 'none',
      boxShadow: 'none',
      backgroundColor: 'transparent',
    },
    borderless: {
      border: 0,
      boxShadow: '0px 1px 4px 0px rgba(0,0,0,.04)',
      borderRadius: '10px',
      backgroundColor: getBackgroundTheme().block2,
      [breakpoints.down(768.05)]: {
        borderRadius: '6px',
        boxShadow: '0px 1px 4px 0px rgba(0,0,0,.04)',
        margin: spacing.MD,
      },
    },
  };

  return styles[panelStyle];
};

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const StyledPanel = styled('div')<IStyledPanel>(
  {
    marginBottom: spacing.MD,
  },

  ({ padding }) => ({
    padding: PADDING[padding as keyof IPadding],
  }),
  ({ withHeader }) => ({
    paddingTop: withHeader && 0,
  }),

  ({ panelStyle, isLogin, isOtherProfile }) => PANEL_STYLE(panelStyle, isLogin, isOtherProfile),

  ({ customStyle }) => customStyle,

  ({ isRowMobile }: CustomStyledProps) => {
    if (isRowMobile === true) {
      return {
        [breakpoints.down(479.05)]: {
          borderRadius: 0,
          borderLeftWidth: 0,
          borderRightWidth: 0,
        },
      };
    }
  }
);

const PanelHeader = styled(Header)(
  {
    flex: '10 1 0%',
    padding: '5px 0',
  },
  ({ panelStyle }: { panelStyle: string }) =>
    panelStyle === 'transparent' && {
      [breakpoints.down(768.05)]: {
        paddingLeft: 16,
        paddingRight: 16,
      },
    }
);

const HeaderContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
});

const CancelContainer = styled('div')({
  flex: 1,
  position: 'absolute',
  right: 0,
  paddingBottom: 8,
  [breakpoints.down(620.05)]: {
    paddingBottom: 4,
  },
});

const Panel = (props: IPanelProps): ReactElement => {
  const {
    children,
    title,
    panelStyle = 'default',
    editable,
    toggleEditable,
    dataTestId = 'edit-about-info',
    isLogin = false,
    cancelButton = null,
    customComponent,
    customPanelHeaderStyle,
    disabled = false,
    customTitleStyle = {},
    ...rest
  } = props;

  return (
    <StyledPanel
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      isLogin
      panelStyle={panelStyle}
      {...rest}
    >
      <HeaderContainer>
        {title && (
          <PanelHeader bottomRuled panelStyle={panelStyle} style={customPanelHeaderStyle}>
            <Title style={{ ...(isLogin ? { color: 'rgba(0, 0, 0, 0.6)' } : {}), ...customTitleStyle }} size="lg">
              {title}
            </Title>
            {customComponent && customComponent}
          </PanelHeader>
        )}
        {editable ? (
          <EditButton
            handleClick={toggleEditable}
            dataTestId={dataTestId}
            customStyle={{ position: 'absolute', right: 0 }}
            disabled={disabled}
          />
        ) : cancelButton ? (
          <CancelContainer>{cancelButton}</CancelContainer>
        ) : (
          ''
        )}
      </HeaderContainer>
      {children}
    </StyledPanel>
  );
};

Panel.defaultProps = {
  padding: 'medium',
  customStyle: {},
};

export default Panel;
